import * as Cesium from 'cesium';

/**
 * Manages aircraft trails in 3D
 */
export class TrailManager3D {
  constructor(viewer, aircraftManager3D) {
    this.viewer = viewer;
    this.aircraftManager3D = aircraftManager3D;
    // cookie -> { primitive: Primitive, positionsHash: string }
    this.trails = new Map();
    this.pendingTrails = new Map();
    this.pendingTrailSamples = new Map();
    this.hiddenCookies = new Set(); // Track hidden aircraft for visibility toggle
    this.maxPoints = 1000;
    this.widthPx = 4; // thicker trail
    this.samplesPerSegment = 1; // keep committed trail cheaper; smooth head handles recent motion
    this.replayTrailHeadWindowMs = 8000;
    this.replayTrailHeadSampleMs = 100;
    this.replayTrailHeadMinDistanceM = 8;
  }

  getAltitudeMeters(position) {
    if (position?.alt_m != null) {
      return position.alt_m;
    }
    if (position?.altitude_m != null) {
      return position.altitude_m;
    }
    if (position?.altitude_ft != null) {
      return position.altitude_ft * 0.3048;
    }
    return 0;
  }

  getCartesianPosition(position) {
    return Cesium.Cartesian3.fromDegrees(
      position.lon,
      position.lat,
      this.getAltitudeMeters(position)
    );
  }

  getPositionTimestampMs(position) {
    const timestamp = position?.timestamp;
    if (timestamp instanceof Date) {
      return timestamp.getTime();
    }
    if (timestamp) {
      const parsedMs = new Date(timestamp).getTime();
      if (Number.isFinite(parsedMs)) {
        return parsedMs;
      }
    }
    return Date.now();
  }

  clearPendingTrailSamples(cookie) {
    this.pendingTrailSamples.delete(cookie);
  }

  updatePendingTrailSamples(cookie, position) {
    if (!position || !Number.isFinite(position.lat) || !Number.isFinite(position.lon)) {
      return [];
    }

    const timestampMs = this.getPositionTimestampMs(position);
    let samples = this.pendingTrailSamples.get(cookie) || [];
    const lastSample = samples[samples.length - 1];

    if (lastSample && timestampMs < lastSample.timestampMs - 250) {
      samples = [];
    }

    if (samples.length === 0) {
      samples = [{ ...position, timestampMs }];
    } else {
      const elapsedMs = timestampMs - lastSample.timestampMs;
      const distanceMeters = Cesium.Cartesian3.distance(
        this.getCartesianPosition(lastSample),
        this.getCartesianPosition(position)
      );
      const shouldAppend =
        elapsedMs >= this.replayTrailHeadSampleMs ||
        distanceMeters >= this.replayTrailHeadMinDistanceM;

      if (shouldAppend) {
        samples.push({ ...position, timestampMs });
      } else {
        samples[samples.length - 1] = { ...position, timestampMs };
      }
    }

    while (samples.length > 1 && (timestampMs - samples[0].timestampMs) > this.replayTrailHeadWindowMs) {
      samples.shift();
    }

    this.pendingTrailSamples.set(cookie, samples);
    return samples;
  }
  
  /**
   * Build the full trail primitive from positions array.
   * Extracted so both updateTrail and forceRebuildTrail can use it.
   */
  _buildTrailPrimitive(cookie, limitedPositions, colorScheme, baseColor) {
    // Convert to Cartesian3 with altitude
    const cart = limitedPositions.map(pos => this.getCartesianPosition(pos));

    if (cart.length < 2) return;

    // Helper: linear interpolate metrics for color
    const lerp = (a, b, t) => a + (b - a) * t;

    // Catmull-Rom interpolation (component-wise, reuse scratch object)
    const scratchResult = new Cesium.Cartesian3();
    const catmullRom = (p0, p1, p2, p3, t) => {
      const t2 = t * t;
      const t3 = t2 * t;
      scratchResult.x = 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * t + (2*p0.x - 5*p1.x + 4*p2.x - p3.x) * t2 + (-p0.x + 3*p1.x - 3*p2.x + p3.x) * t3);
      scratchResult.y = 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * t + (2*p0.y - 5*p1.y + 4*p2.y - p3.y) * t2 + (-p0.y + 3*p1.y - 3*p2.y + p3.y) * t3);
      scratchResult.z = 0.5 * ((2 * p1.z) + (-p0.z + p2.z) * t + (2*p0.z - 5*p1.z + 4*p2.z - p3.z) * t2 + (-p0.z + 3*p1.z - 3*p2.z + p3.z) * t3);
      return Cesium.Cartesian3.clone(scratchResult);
    };

    // Keep old primitive reference for deferred removal after new one is ready
    const existing = this.trails.get(cookie);
    const oldPrimitive = existing?.primitive || null;

    // Build smoothed polyline positions
    const smoothed = [];
    for (let i = 0; i < cart.length - 1; i++) {
      const p0 = cart[Math.max(0, i - 1)];
      const p1 = cart[i];
      const p2 = cart[i + 1];
      const p3 = cart[Math.min(cart.length - 1, i + 2)];
      for (let s = 0; s < this.samplesPerSegment; s++) {
        const t = s / this.samplesPerSegment;
        smoothed.push(catmullRom(p0, p1, p2, p3, t));
      }
    }
    smoothed.push(cart[cart.length - 1]);

    // Build color per vertex array
    const colors = [];

    // Optimization: For 'aircraft' color scheme, use baseColor directly (much faster)
    if (colorScheme === 'aircraft') {
      const aircraftColor = baseColor || Cesium.Color.WHITE;
      const colorWithAlpha = aircraftColor.withAlpha(0.9);
      const totalVertices = (cart.length - 1) * this.samplesPerSegment + 1;
      for (let i = 0; i < totalVertices; i++) {
        colors.push(colorWithAlpha);
      }
    } else {
      // For other color schemes, compute colors per vertex
      for (let i = 0; i < cart.length - 1; i++) {
        const a = limitedPositions[i];
        const b = limitedPositions[i + 1];
        for (let s = 0; s < this.samplesPerSegment; s++) {
          const t = s / this.samplesPerSegment;
          const synth = {
            alt_m: (a.alt_m != null && b.alt_m != null) ? lerp(a.alt_m, b.alt_m, t) : undefined,
            altitude_m: (a.altitude_m != null && b.altitude_m != null) ? lerp(a.altitude_m, b.altitude_m, t) : undefined,
            altitude_ft: (a.altitude_ft != null && b.altitude_ft != null) ? lerp(a.altitude_ft, b.altitude_ft, t) : undefined,
            vario_mps: (a.vario_mps != null && b.vario_mps != null) ? lerp(a.vario_mps, b.vario_mps, t) : undefined,
            vario_avg_mps: (a.vario_avg_mps != null && b.vario_avg_mps != null) ? lerp(a.vario_avg_mps, b.vario_avg_mps, t) : undefined,
            vario_kalman_mps: (a.vario_kalman_mps != null && b.vario_kalman_mps != null) ? lerp(a.vario_kalman_mps, b.vario_kalman_mps, t) : undefined,
            speed_mps: (a.speed_mps != null && b.speed_mps != null) ? lerp(a.speed_mps, b.speed_mps, t) : undefined,
            speed_kt: (a.speed_kt != null && b.speed_kt != null) ? lerp(a.speed_kt, b.speed_kt, t) : undefined,
            etfDeltaSeconds: (a.etfDeltaSeconds != null && b.etfDeltaSeconds != null) ? lerp(a.etfDeltaSeconds, b.etfDeltaSeconds, t) : undefined
          };
          colors.push(this.getTrailColor(synth, colorScheme, baseColor).withAlpha(0.9));
        }
      }
      colors.push(this.getTrailColor(limitedPositions[limitedPositions.length - 1], colorScheme, baseColor).withAlpha(0.9));
    }

    const geometry = new Cesium.PolylineGeometry({
      positions: smoothed,
      width: this.widthPx,
      colors: colors,
      colorsPerVertex: true,
      arcType: Cesium.ArcType.NONE  // NONE is much faster than GEODESIC for real-time updates
    });
    const instance = new Cesium.GeometryInstance({ geometry });
    const primitive = new Cesium.Primitive({
      geometryInstances: instance,
      appearance: new Cesium.PolylineColorAppearance({ translucent: true }),
      asynchronous: false  // Synchronous to avoid trails disappearing from failed async compilation
    });

    primitive.show = !this.hiddenCookies.has(cookie);

    // Remove old primitive immediately before adding new one (synchronous compilation means new one is ready)
    if (oldPrimitive) {
      try {
        this.viewer.scene.primitives.remove(oldPrimitive);
      } catch (e) {
        console.warn('Failed to remove old trail primitive:', e);
      }
    }

    this.viewer.scene.primitives.add(primitive);

    this.trails.set(cookie, {
      primitive,
      pointCount: limitedPositions.length,
      colorScheme,
      baseColor,
      lastPosition: limitedPositions[limitedPositions.length - 1]  // Track last position to detect updates
    });
  }

  /**
   * Update trail for an aircraft.
   * Skips rebuild when point count and color scheme are unchanged.
   */
  updateTrail(cookie, positions, colorScheme = 'aircraft', baseColor = null) {
    if (!positions || positions.length === 0) return;

    const limitedPositions = this.maxPoints > 0
      ? positions.slice(-this.maxPoints)
      : positions;

    if (limitedPositions.length < 2) return;

    // Check if we need to rebuild the trail
    const existing = this.trails.get(cookie);

    // Always rebuild if:
    // 1. No existing trail, OR
    // 2. Color scheme changed, OR
    // 3. Point count changed, OR
    // 4. Latest position changed (check last position's timestamp/coordinates)
    let shouldRebuild = !existing || !existing.primitive ||
                        existing.colorScheme !== colorScheme ||
                        existing.pointCount !== limitedPositions.length;

    if (!shouldRebuild && existing.lastPosition) {
      const lastPos = limitedPositions[limitedPositions.length - 1];
      const prevLastPos = existing.lastPosition;
      // Rebuild if the most recent position has changed
      shouldRebuild = lastPos.lat !== prevLastPos.lat ||
                      lastPos.lon !== prevLastPos.lon ||
                      (lastPos.alt_m || lastPos.altitude_m || 0) !== (prevLastPos.alt_m || prevLastPos.altitude_m || 0);
    }

    if (!shouldRebuild) {
      return;
    }

    this._buildTrailPrimitive(cookie, limitedPositions, colorScheme, baseColor);
  }

  updatePendingTrail(cookie, fromPosition, toPosition, colorScheme = 'aircraft', baseColor = null) {
    if (!fromPosition || !toPosition) {
      this.clearPendingTrail(cookie);
      return;
    }

    if (
      !Number.isFinite(fromPosition.lat) ||
      !Number.isFinite(fromPosition.lon) ||
      !Number.isFinite(toPosition.lat) ||
      !Number.isFinite(toPosition.lon)
    ) {
      this.clearPendingTrail(cookie);
      return;
    }

    const pendingSamples = this.updatePendingTrailSamples(cookie, toPosition);
    const pendingPositions = [];
    const fromCartesian = this.getCartesianPosition(fromPosition);
    pendingPositions.push(fromCartesian);
    pendingSamples.forEach(sample => {
      pendingPositions.push(this.getCartesianPosition(sample));
    });

    // Avoid duplicate leading points when the first sampled point matches the commit anchor.
    if (pendingPositions.length >= 2) {
      const firstDistance = Cesium.Cartesian3.distance(pendingPositions[0], pendingPositions[1]);
      if (firstDistance <= 0.5) {
        pendingPositions.shift();
      }
    }

    if (pendingPositions.length < 2) {
      this.clearPendingTrail(cookie);
      return;
    }

    const distanceMeters = Cesium.Cartesian3.distance(
      pendingPositions[pendingPositions.length - 2],
      pendingPositions[pendingPositions.length - 1]
    );
    if (!(distanceMeters > 0.5)) {
      this.clearPendingTrail(cookie);
      return;
    }

    const pendingColor = this.getTrailColor(toPosition, colorScheme, baseColor).withAlpha(0.9);
    let entity = this.pendingTrails.get(cookie);

    if (!entity) {
      entity = this.viewer.entities.add({
        id: `trail-pending-${cookie}`,
        show: !this.hiddenCookies.has(cookie),
        polyline: {
          positions: pendingPositions,
          width: this.widthPx,
          material: pendingColor,
          arcType: Cesium.ArcType.NONE  // NONE is much faster than GEODESIC for real-time updates
        }
      });
      this.pendingTrails.set(cookie, entity);
      return;
    }

    entity.show = !this.hiddenCookies.has(cookie);
    entity.polyline.positions = pendingPositions;
    entity.polyline.material = pendingColor;
  }
  
  /**
   * Get trail color based on scheme
   */
  getTrailColor(position, colorScheme, baseColor) {
    // Use the aircraft manager's color logic
    const cesiumColor = this.aircraftManager3D.getColorForAircraft(position, baseColor);
    if (!cesiumColor) {
      return Cesium.Color.TRANSPARENT;
    }
    
    // Add some transparency for trails
    return cesiumColor.withAlpha(0.8);
  }
  
  /**
   * Remove trail for an aircraft
   */
  removeTrail(cookie) {
    const trail = this.trails.get(cookie);
    if (trail && trail.primitive) {
      this.viewer.scene.primitives.remove(trail.primitive);
    }
    this.trails.delete(cookie);
    this.clearPendingTrail(cookie);
  }
  
  /**
   * Set maximum trail points
   */
  setMaxPoints(maxPoints) {
    this.maxPoints = maxPoints;
  }

  /**
   * Set hidden cookies and apply visibility to all trails
   * @param {Set} hiddenSet - Set of cookies that should be hidden
   */
  setHiddenCookies(hiddenSet) {
    this.hiddenCookies = new Set(hiddenSet);

    // Apply visibility to all trail primitives
    this.trails.forEach((trail, cookie) => {
      const shouldShow = !this.hiddenCookies.has(cookie);
      if (trail.primitive) {
        trail.primitive.show = shouldShow;
      }
    });

    this.pendingTrails.forEach((entity, cookie) => {
      entity.show = !this.hiddenCookies.has(cookie);
    });
  }

  /**
   * Check if a trail is hidden
   * @param {string|number} cookie - The aircraft cookie
   * @returns {boolean} True if hidden
   */
  isHidden(cookie) {
    return this.hiddenCookies.has(cookie);
  }

  /**
   * Clear all trails
   */
  clearAll() {
    this.trails.forEach((trail) => {
      if (trail.primitive) {
        this.viewer.scene.primitives.remove(trail.primitive);
      }
    });
    this.trails.clear();
    this.pendingTrails.forEach((entity) => {
      this.viewer.entities.remove(entity);
    });
    this.pendingTrails.clear();
    this.pendingTrailSamples.clear();
  }

  clearPendingTrail(cookie) {
    const entity = this.pendingTrails.get(cookie);
    if (entity) {
      this.viewer.entities.remove(entity);
      this.pendingTrails.delete(cookie);
    }
    this.clearPendingTrailSamples(cookie);
  }
}
